import { Typography } from 'components/common';
import Icons from 'const/icons';
import Image from 'next/image';
import React from 'react';
import clsx from 'clsx';
import { AvatarSocialProof } from '@/components/common/avatar-social-proof';
import { AvatarItem } from '@/components/common/avatar/avatarGroup';
import { StatCard } from '@/components/common/stat-card';

const AVATAR_ITEMS: AvatarItem[] = [
	{ url: Icons.userThree },
	{ url: Icons.userTwo },
	{ url: Icons.userOne },
];

const HOW_IT_WORKS = [
	{
		step: 1,
		title: 'Create an account',
		description: 'Securely sign up in seconds to access the platform.',
	},
	{
		step: 2,
		title: 'Check Eligibility',
		description: 'See if you qualify for active claims instantly.',
	},
	{
		step: 3,
		title: 'Get Paid When You Win',
		description:
			'Money directly to your bank account if we recover on your behalf.',
	},
];

export const SignUpRightPanel: React.FC = () => {
	return (
		<div className="relative flex flex-col justify-center h-full px-10 xl:px-[108px] py-10 overflow-hidden">
			<Image
				src="/icons/svg/bubbles.svg"
				alt=""
				width={515}
				height={731}
				className="absolute bottom-0 right-0 pointer-events-none select-none"
				aria-hidden
			/>
			<div className="mb-6">
				<Typography type="semibold-16" className="text-gray-35 ml-10">
					Welcome to
				</Typography>
				<Image
					src="/logos/logo.webp"
					alt="ClaimMagic"
					width={199}
					height={38}
					className="object-contain"
				/>
			</div>

			<h1
				className="font-bold text-black mb-4"
				style={{
					fontFamily: 'Poppins, sans-serif',
					fontSize: '60px',
					lineHeight: '110%',
					letterSpacing: '-2%',
				}}
			>
				Claim What&apos;s <span className="text-primary">Yours</span>
			</h1>

			<p
				className="text-[#374151] mb-6"
				style={{
					fontFamily: 'Poppins, sans-serif',
					fontSize: '18px',
					lineHeight: '150%',
					letterSpacing: '0px',
				}}
			>
				We help you file, track, and get paid what you&apos;re owed for legal
				claims—all in one place.
			</p>

			<div className="grid grid-cols-2 gap-4 mb-6">
				<StatCard
					icon={Icons.tablerIconGavel}
					value="+50"
					label="Years representing plaintiffs"
					description="Experienced lawyers"
					iconVariant="blue"
				/>
				<StatCard
					icon={Icons.shieldSecurity}
					value="100%"
					label="Confidential"
					description="All communications are protected"
					iconVariant="purple"
				/>
			</div>

			<Typography
				type="semibold-14"
				className="text-[#6B7280] uppercase tracking-widest mb-4 text-xs"
			>
				How it works
			</Typography>

			<div className="flex flex-col gap-0 mb-6 relative">
				<div
					className="absolute left-5 top-0 -translate-x-1/2 w-[2px] pointer-events-none"
					style={{
						height: '188px',
						background:
							'linear-gradient(180deg, rgba(221, 214, 254, 0) 0%, #DDD6FE 50%, rgba(221, 214, 254, 0) 100%)',
					}}
				/>
				{HOW_IT_WORKS.map((item, index) => (
					<div key={item.step} className="flex gap-4">
						<div className="flex flex-col items-center">
							<div
								className={clsx(
									'w-10 h-10 rounded-full bg-white border-2 flex items-center justify-center shrink-0 z-10',
									index === 0 ? 'border-primary' : 'border-[#DDD6FE]'
								)}
							>
								<Typography
									type="semibold-16"
									className={index === 0 ? 'text-primary' : 'text-[#9CA3AF]'}
								>
									{item.step}
								</Typography>
							</div>
						</div>
						<div className="pb-5">
							<Typography
								type="semibold-18"
								className="text-black leading-tight"
							>
								{item.title}
							</Typography>
							<Typography type="regular-14" className="text-[#6B7280] mt-0.5">
								{item.description}
							</Typography>
						</div>
					</div>
				))}
			</div>

			<div
				className="rounded-2xl py-3 px-[18px] flex items-center gap-4 bg-[#FFFFFFCC] border border-white"
				style={{
					boxShadow: '0px 1px 2px 0px #0000000D',
					backdropFilter: 'blur(4px)',
				}}
			>
				<AvatarSocialProof
					avatarItems={AVATAR_ITEMS}
					typographyClassName={'!text-sm'}
				/>
			</div>
		</div>
	);
};
